/*    
<summary>
  This functional component "AddUserForm" provides form to add new user or update existing user.
  Developer:Aashish Singh, Created Date:29-March-2023
</summary>
<returns>Returns JSX</returns>
*/
import React, { useState } from "react";
import { Form, Formik, Field } from "formik";
import "./add-edit-user-form.css";
import { FormattedMessage } from "react-intl";
import { Modal, Button } from "react-bootstrap";
import { IOption } from "../../../models/ICommon";
import { IAddUser } from "../../../models/Form/IAddUser";
import LoaderButton from "../../../shared-components/Button/LoaderButton";
import FormLoader from "../../../shared-components/FormLoader/FormLoader";
import FormikFormInput from "../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormSelect from "../../../shared-components/FormikFormHandlers/FormikFormSelect";
import Label from "../../../shared-components/Label/Label";
import {
  AddUserValidateSchema,
  EditUserValidateSchema,
} from "../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import toast from "react-hot-toast";
import { formatMessage } from "../../../translations/format-message";
import config from "../../../helpers/config-helper";
import { getThemeFromLocalStorage } from "../../../helpers/local-stotrage-helper";
import { Tooltip } from "react-tooltip";

interface IProps {
  id: number;
  roleOptions: IOption[];
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getUserLoading: boolean;
  addUpdateUserLoading: boolean;
  initialValues: IAddUser;
}

const appConfig = config();
const is2FEnabled = appConfig.IS_2F_ENABLED;

const AddUserForm = (props: IProps) => {
  const [showHidePassword, setShowHidePassword] = useState(false);
  return (
    <>
      <Modal
        className="add-user-modal correction-modal fancy-modal"
        show={true}
        centered
        onHide={() => {}}
      >
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="user_title_add_user" />
            ) : (
              <FormattedMessage id="user_title_edit_user" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.addUpdateUserLoading}
              className="Crossicons"
              title={formatMessage("close")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={props.initialValues}
          enableReinitialize
          validationSchema={
            props.id === -1 ? AddUserValidateSchema : EditUserValidateSchema
          }
          validateOnBlur={false}
          onSubmit={async (values) => {
            props.submitHandler(values);
          }}
        >
          {({ dirty }) => {
            return (
              <Form className="user-form">
                {props.getUserLoading || props.addUpdateUserLoading ? (
                  <FormLoader
                    loading={props.getUserLoading || props.addUpdateUserLoading}
                  />
                ) : null}
                <Modal.Body>
                  <div className="form-group mb-1 row">
                    <Label
                      className="label-style col-sm-4 col-form-label"
                      required={true}
                      label="user_label_user_name"
                    />
                    <div className="col-sm-8">
                      <Field
                        data-testid="testEmail"
                        className="form-control"
                        name="UserName"
                        type="text"
                        validcharacter={["@", "#", "$", "-", "_", "."]}
                        placeholder="user_label_user_name"
                        as={FormikFormInput}
                      />
                    </div>
                  </div>
                  <div className="form-group mb-1 row">
                    <Label
                      className="label-style col-sm-4 col-form-label"
                      required={true}
                      label="user_label_login_id"
                    />
                    <div className="col-sm-8">
                      <Field
                        data-testid="testEmail"
                        className="form-control"
                        name="Email"
                        type="text"
                        invalidcharacter={[" "]}
                        validcharacter={[
                          "-",
                          "_",
                          "[",
                          "]",
                          "(",
                          ")",
                          ":",
                          "#",
                          "@",
                          ".",
                        ]}
                        placeholder="user_label_login_id"
                        as={FormikFormInput}
                      />
                    </div>
                  </div>
                  <div className="form-group mb-1 row">
                    <Label
                      className="label-style col-sm-4 col-form-label"
                      required={true}
                      label="role"
                    />
                    <div className="col-sm-8">
                      <Field
                        name="Role.Id"
                        as={FormikFormSelect}
                        options={props.roleOptions}
                      />
                    </div>
                  </div>
                  {props.id === -1 && (
                    <>
                      <div className="form-group row">
                        <Label
                          className="label-style col-sm-4 col-form-label"
                          required={true}
                          label="user_label_password"
                        />
                        <div className="col-sm-8">
                          <Field
                            data-testid="testPassword"
                            className="form-control"
                            name="Password"
                            autoComplete="new-password"
                            type={showHidePassword ? "text" : "password"}
                            invalidcharacter={[" "]}
                            validcharacter={[
                              "~",
                              "`",
                              "!",
                              "@",
                              "#",
                              "$",
                              "%",
                              "^",
                              "&",
                              "*",
                              "(",
                              ")",
                              "_",
                              "-",
                              "+",
                              "=",
                              "{",
                              "[",
                              "}",
                              "]",
                              "|",
                              "\\",
                              ":",
                              ";",
                              "'",
                              '"',
                              "<",
                              ",",
                              ">",
                              ".",
                              "?",
                              "/",
                            ]}
                            placeholder="user_label_password"
                            as={FormikFormInput}
                          />
                          {showHidePassword ? (
                            <svg
                              type="button"
                              onClick={() => {
                                setShowHidePassword(!showHidePassword);
                              }}
                              viewBox="0 0 24 24"
                              width="20"
                              height="20"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="showHidePass css-i6dzq1 modalBox"
                            >
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                              <circle cx="12" cy="12" r="3"></circle>
                            </svg>
                          ) : (
                            <svg
                              type="button"
                              onClick={() => {
                                setShowHidePassword(!showHidePassword);
                              }}
                              viewBox="0 0 24 24"
                              width="20"
                              height="20"
                              stroke="currentColor"
                              strokeWidth="2"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="showHidePass css-i6dzq1 modalBox"
                            >
                              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                              <line x1="1" y1="1" x2="23" y2="23"></line>
                            </svg>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                  {is2FEnabled && (
                    <div className="form-group row switchBox">
                      <div className="col-sm-8 offset-sm-4">
                        <div className="form-check form-switch">
                          <Field
                            data-testid="testRememberMe"
                            className="form-check-input"
                            name="IsTwoFactorEnable"
                            type="checkbox"
                            id="IsTwoFactorEnable"
                          />
                          <label
                            htmlFor="IsTwoFactorEnable"
                            className="form-check-label"
                          >
                            <FormattedMessage id="two_factor_enable" />
                          </label>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            fill="currentColor"
                            className="bi bi-info-circle ms-2"
                            viewBox="0 0 16 16"
                            data-tooltip-id="two_factor_enable_tooltip"
                            data-tooltip-html={formatMessage(
                              "two_factor_enable_info"
                            )}
                            data-tooltip-variant="dark"
                          >
                            <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                          </svg>
                          <Tooltip
                            id="two_factor_enable_tooltip"
                            place="top"
                            className={
                              getThemeFromLocalStorage()
                                ? "dark-theme-wrap"
                                : ""
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="form-group row switchBox">
                    <div className="col-sm-8 offset-sm-4">
                      <div className="form-check form-switch">
                        <Field
                          data-testid="testRememberMe"
                          className="form-check-input"
                          name="IsActive"
                          type="checkbox"
                          id="IsActive"
                        />
                        <label htmlFor="IsActive" className="form-check-label">
                          <FormattedMessage id="is_active" />
                        </label>
                      </div>
                    </div>
                  </div>
                </Modal.Body>
                <div className="modal-footer pt-0 border-0">
                  <div className="form-group row g-0 tab-action">
                    <div className="col-12">
                      <LoaderButton
                        type="submit"
                        // type={props.id < 0 ? "submit" : (dirty ? "submit" :"button")}
                        // onClick={props.id < 0 ? undefined : (dirty ? undefined : ()=>{
                        //   // toast.success(formatMessage("no_change_detected"));
                        //   props.onModalClosed();
                        // })}
                        isLoading={props.addUpdateUserLoading}
                        text={props.id > 0 ? "update" : "button_save"}
                      />
                      <Button
                        variant="secondary"
                        type="button"
                        onClick={props.onModalClosed}
                        disabled={props.addUpdateUserLoading}
                      >
                        <FormattedMessage id="button_cancel" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default AddUserForm;
